import React, { useMemo, useState } from 'react';
import { Box, Flex, Text, Button, HStack } from '@chakra-ui/react';
import Toolbar from './Toolbar';
import AnnotationLayer from './AnnotationLayer';
import ImageRenderer from './ImageRenderer';
import PdfRenderer from './PdfRenderer';

const DEFAULT_MAX_WIDTH = 460;

function isPdfFile(file) {
  if (!file) return false;
  const mime = (file.mime || file.type || '').toLowerCase();
  if (mime.includes('pdf')) return true;
  const url = (file.url || file.src || '').toLowerCase();
  return url.endsWith('.pdf');
}

function nextId() {
  return `ann-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export default function AnnotatableDocument({
  files = [],
  value,
  onChange,
  readonly = false,
  maxWidth = DEFAULT_MAX_WIDTH,
}) {
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#1d9e75');
  const [fileIndex, setFileIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [dimensions, setDimensions] = useState({ width: maxWidth, height: 600 });

  const currentFile = files[fileIndex] || null;
  const fileIsPdf = isPdfFile(currentFile);

  const items = useMemo(() => (value?.items || []), [value]);

  const visibleAnnotations = useMemo(
    () =>
      items.filter(
        (a) => (a.fileIndex ?? 0) === fileIndex && (a.page ?? 1) === page
      ),
    [items, fileIndex, page]
  );

  const counts = useMemo(() => {
    const c = { total: 0, pen: 0, text: 0, check: 0, x: 0 };
    for (const a of items) {
      c.total += 1;
      if (a.type === 'pen') c.pen += 1;
      else if (a.type === 'text') c.text += 1;
      else if (a.type === 'check') c.check += 1;
      else if (a.type === 'x') c.x += 1;
    }
    return c;
  }, [items]);

  const emit = (nextItems) => {
    onChange?.({ version: 1, items: nextItems });
  };

  const handleAdd = (annotation) => {
    const item = {
      id: nextId(),
      fileIndex,
      page,
      ...annotation,
    };
    emit([...items, item]);
  };

  const handleRemove = (id) => {
    emit(items.filter((a) => a.id !== id));
  };

  const handleUndo = () => {
    if (items.length === 0) return;
    const lastForCurrent = [...items]
      .reverse()
      .find((a) => (a.fileIndex ?? 0) === fileIndex && (a.page ?? 1) === page);
    if (lastForCurrent) {
      emit(items.filter((a) => a.id !== lastForCurrent.id));
    }
  };

  const handleClearAll = () => {
    if (!visibleAnnotations.length) return;
    if (!window.confirm('¿Eliminar todas las anotaciones de esta página?')) return;
    emit(items.filter((a) => !((a.fileIndex ?? 0) === fileIndex && (a.page ?? 1) === page)));
  };

  if (!currentFile) {
    return (
      <Box p={4} bg="gray.50" borderRadius="md" fontSize="sm" color="gray.600">
        No hay archivos para mostrar.
      </Box>
    );
  }

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      {!readonly && (
        <Toolbar
          tool={tool}
          setTool={setTool}
          color={color}
          setColor={setColor}
          onUndo={handleUndo}
          onClearAll={handleClearAll}
        />
      )}

      {(files.length > 1 || fileIsPdf) && (
        <Flex
          px={3}
          py={2}
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
          gap={3}
          alignItems="center"
          flexWrap="wrap"
          fontSize="12px"
        >
          {files.length > 1 && (
            <HStack spacing={1}>
              <Text color="gray.500">Archivo:</Text>
              {files.map((f, idx) => (
                <Button
                  key={idx}
                  size="xs"
                  variant={idx === fileIndex ? 'solid' : 'outline'}
                  bg={idx === fileIndex ? '#574feb' : 'white'}
                  color={idx === fileIndex ? 'white' : 'gray.700'}
                  _hover={{ bg: idx === fileIndex ? '#3c3489' : 'gray.100' }}
                  onClick={() => {
                    setFileIndex(idx);
                    setPage(1);
                  }}
                >
                  {idx + 1}
                </Button>
              ))}
            </HStack>
          )}
          {fileIsPdf && numPages > 1 && (
            <HStack spacing={1}>
              <Text color="gray.500">Página:</Text>
              <Button
                size="xs"
                variant="outline"
                isDisabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </Button>
              <Text>
                {page} / {numPages}
              </Text>
              <Button
                size="xs"
                variant="outline"
                isDisabled={page >= numPages}
                onClick={() => setPage((p) => Math.min(numPages, p + 1))}
              >
                ›
              </Button>
            </HStack>
          )}
          <Box flex="1" />
          {!readonly && counts.total > 0 && (
            <Text color="gray.500">
              {counts.total} anotaciones · {counts.check} ✓ · {counts.x} ✗
            </Text>
          )}
        </Flex>
      )}

      <Box bg="#f1efe8" px={4} py={4} display="flex" justifyContent="center">
        <Box
          position="relative"
          maxW={`${maxWidth}px`}
          width="100%"
          bg="white"
          boxShadow="0 0 0 1px #d1d5db"
          borderRadius="4px"
        >
          {fileIsPdf ? (
            <PdfRenderer
              src={currentFile.url}
              page={page}
              width={maxWidth}
              onLoadPage={(dim) => dim && setDimensions(dim)}
              onLoadDocument={(d) => setNumPages(d.numPages || 1)}
            />
          ) : (
            <ImageRenderer
              src={currentFile.url}
              maxWidth={maxWidth}
              onLoad={(dim) => setDimensions({ width: dim.width, height: dim.height })}
            />
          )}
          <AnnotationLayer
            annotations={visibleAnnotations}
            tool={tool}
            color={color}
            onAdd={handleAdd}
            onRemove={handleRemove}
            width={dimensions.width}
            height={dimensions.height}
            readonly={readonly}
          />
        </Box>
      </Box>
    </Box>
  );
}
